import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useAuthTenant } from 'store/auth';
import { Box, Grid, Card, CardContent, Typography, CircularProgress } from '@mui/material';

import { getAreas } from 'services/areas-service';
import { getMapCompanyBySlug } from 'services/company-service';

import { CompanyCard } from 'Components/CompanyCard';
import { MappingCard } from 'Components/MappingCard';
import { Main } from '../../Components/Main';
import Graphic from './Graphic';
import { getAssets } from 'services/assets-service';
import { getSystems } from 'services/systems-service';
import { getProcesses } from 'services/processes-service';
import { PageHelper } from '../../Components/PageHelper';

export const Map = () => {
  const selectedTenant = useSelector(useAuthTenant);
  const { t } = useTranslation(['mapping', 'common']);
  const nodeTypes = useMemo(() => ({ company: CompanyCard, section: MappingCard }), []);

  // Tour steps — using t() so they update reactively on language change
  const tourSteps = useMemo(() => [
    {
      target: 'body',
      content: t('mapping:tour.welcome'),
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '[data-tour="stats-cards"]',
      content: t('mapping:tour.statsCards'),
      disableScrolling: true,
    },
    {
      target: '[data-tour="map-visualization"]',
      content: t('mapping:tour.visualization'),
    },
    {
      target: '[data-tour="map-legend"]',
      content: t('mapping:tour.legend'),
      disableScrolling: true,
    },
    {
      target: '[data-tour="map-info"]',
      content: t('mapping:tour.info'),
      disableScrolling: true,
    },
    {
      target: '[data-tour="details-tables"]',
      content: t('mapping:tour.details'),
    },
    {
      target: 'body',
      content: t('mapping:tour.done'),
      placement: 'center',
    },
  ], [t]);

  const { data: companyMap, isSuccess: companyMapSuccess, isLoading: companyMapLoading } = useQuery(['getMapCompanyBySlug'], async () => {
    if (selectedTenant.slug === undefined) return [];
    const response = await getMapCompanyBySlug(selectedTenant.slug);
    return response?.data;
  });

  const { data: companyAreas, isSuccess: areaSuccess, isLoading: areasLoading } = useQuery(['getAreas'], async () => {
    if (selectedTenant.slug === undefined) return [];
    const result = await getAreas(selectedTenant.slug);
    return result?.data.length > 0 ? result?.data : [];
  });

  const { data: companyProcesses, isSuccess: processSuccess, isLoading: processesLoading } = useQuery(['getProcesses'], async () => {
    if (selectedTenant.slug === undefined) return [];
    const result = await getProcesses(selectedTenant.slug);
    return result?.data.length > 0 ? result?.data : [];
  });

  const { data: companySystems, isSuccess: systemSuccess, isLoading: systemsLoading } = useQuery(['getSystems'], async () => {
    if (selectedTenant.slug === undefined) return [];
    const result = await getSystems(selectedTenant.slug);
    return result?.data.length > 0 ? result?.data : [];
  });

  const { data: companyAssets, isSuccess: assetSuccess, isLoading: assetsLoading } = useQuery(['getAssets'], async () => {
    if (selectedTenant.slug === undefined) return [];
    const result = await getAssets(selectedTenant.slug);
    return result?.data.length > 0 ? result?.data : [];
  });

  const areasCount = companyAreas?.length || 0;
  const processesCount = companyProcesses?.length || 0;
  const systemsCount = companySystems?.length || 0;
  const assetsCount = companyAssets?.length || 0;

  const isLoading = companyMapLoading || areasLoading || processesLoading || systemsLoading || assetsLoading;
  const allDataLoaded = companyMapSuccess && areaSuccess && processSuccess && systemSuccess && assetSuccess;

  const mapData = useMemo(() => {
    if (!companyAreas?.length) return null;

    return companyAreas.map(area => ({
      id: area._id,
      name: area.name,
      description: area.description,
      user: area.user,
      criticity: area.criticity,
      processes: area.processes || [],
      systems: area.systems || [],
      assets: area.assets || [],
      type: 'area'
    }));
  }, [companyAreas]);

  return (
    <>
      <PageHelper
        steps={tourSteps}
        tourKey="map-tour"
        autoStart={true}
        showButton={true}
      />

      <Main>
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" component="h1" sx={{ mb: 1, fontWeight: 'bold' }}>
              {t('mapping:page.title')}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {t('mapping:page.breadcrumb')}
            </Typography>
          </Box>

          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }} data-tour="stats-cards">
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                height: '120px'
              }}>
                <CardContent sx={{ textAlign: 'center', pt: 3 }}>
                  <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : areasCount}
                  </Typography>
                  <Typography variant="body1">
                    {t('mapping:stats.areas')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
                height: '120px'
              }}>
                <CardContent sx={{ textAlign: 'center', pt: 3 }}>
                  <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : processesCount}
                  </Typography>
                  <Typography variant="body1">
                    {t('mapping:stats.processes')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white',
                height: '120px'
              }}>
                <CardContent sx={{ textAlign: 'center', pt: 3 }}>
                  <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : systemsCount}
                  </Typography>
                  <Typography variant="body1">
                    {t('mapping:stats.systems')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                color: 'white',
                height: '120px'
              }}>
                <CardContent sx={{ textAlign: 'center', pt: 3 }}>
                  <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : assetsCount}
                  </Typography>
                  <Typography variant="body1">
                    {t('mapping:stats.assets')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Corporate Map */}
          <Card sx={{ minHeight: '800px', mb: 3 }} data-tour="map-visualization">
            <CardContent>
              <Typography variant="h6" component="h2" sx={{ mb: 3, fontWeight: 'bold' }}>
                {t('mapping:visualization.title')}
              </Typography>

              {isLoading ? (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '700px'
                }}>
                  <CircularProgress size={60} />
                </Box>
              ) : (
                <Box sx={{
                  minHeight: '700px',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {selectedTenant !== undefined && allDataLoaded && (
                    <Graphic
                      tenant={selectedTenant}
                      nodeTypes={nodeTypes}
                      result={companyMap}
                      areas={companyAreas}
                      processes={companyProcesses}
                      systems={companySystems}
                      assets={companyAssets}
                      mapData={mapData}
                    />
                  )}

                  {/* Map info panel */}
                  <Box sx={{
                    position: 'absolute',
                    bottom: 16,
                    left: 16,
                    background: 'rgba(255,255,255,0.9)',
                    p: 2,
                    borderRadius: 1,
                    boxShadow: 1,
                    maxWidth: '300px',
                    maxHeight: '200px',
                    overflow: 'auto'
                  }} data-tour="map-info">
                    <Typography variant="caption" display="block" sx={{ mb: 0.5, wordBreak: 'break-word' }}>
                      <strong>{t('mapping:visualization.info.areas', { count: areasCount })}</strong> {companyAreas?.length > 3
                        ? `${companyAreas.slice(0, 3).map(area => area.name).join(', ')}...`
                        : companyAreas?.map(area => area.name).join(', ') || t('mapping:visualization.info.noneF')}
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ mb: 0.5, wordBreak: 'break-word' }}>
                      <strong>{t('mapping:visualization.info.processes', { count: processesCount })}</strong> {companyProcesses?.length > 3
                        ? `${companyProcesses.slice(0, 3).map(process => process.name).join(', ')}...`
                        : companyProcesses?.map(process => process.name).join(', ') || t('mapping:visualization.info.noneM')}
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ mb: 0.5, wordBreak: 'break-word' }}>
                      <strong>{t('mapping:visualization.info.systems', { count: systemsCount })}</strong> {companySystems?.length > 3
                        ? `${companySystems.slice(0, 3).map(system => system.name).join(', ')}...`
                        : companySystems?.map(system => system.name).join(', ') || t('mapping:visualization.info.noneM')}
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ wordBreak: 'break-word' }}>
                      <strong>{t('mapping:visualization.info.assets', { count: assetsCount })}</strong> {companyAssets?.length > 3
                        ? `${companyAssets.slice(0, 3).map(asset => asset.name).join(', ')}...`
                        : companyAssets?.map(asset => asset.name).join(', ') || t('mapping:visualization.info.noneM')}
                    </Typography>
                  </Box>

                  {/* Map Legend */}
                  <Box sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    background: 'rgba(255,255,255,0.9)',
                    p: 2,
                    borderRadius: 1,
                    boxShadow: 1
                  }} data-tour="map-legend">
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                      {t('mapping:visualization.legend.title')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <Box sx={{ width: 12, height: 12, bgcolor: '#2196f3', borderRadius: 1, mr: 1 }} />
                      <Typography variant="caption">{t('mapping:visualization.legend.areas')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <Box sx={{ width: 12, height: 12, bgcolor: '#e91e63', borderRadius: 1, mr: 1 }} />
                      <Typography variant="caption">{t('mapping:visualization.legend.processes')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <Box sx={{ width: 12, height: 12, bgcolor: '#00bcd4', borderRadius: 1, mr: 1 }} />
                      <Typography variant="caption">{t('mapping:visualization.legend.systems')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ width: 12, height: 12, bgcolor: '#4caf50', borderRadius: 1, mr: 1 }} />
                      <Typography variant="caption">{t('mapping:visualization.legend.assets')}</Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Details tables */}
          <Grid container spacing={3} data-tour="details-tables">
            {/* Areas */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 'bold', color: '#2196f3' }}>
                    {t('mapping:sections.areas', { count: areasCount })}
                  </Typography>
                  <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
                    {companyAreas?.length > 0 ? (
                      companyAreas.map((area) => (
                        <Box key={area._id} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, wordBreak: 'break-word' }}>
                            {area.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, wordBreak: 'break-word' }}>
                            {area.description?.length > 100 ? `${area.description.substring(0, 100)}...` : area.description}
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ wordBreak: 'break-word' }}>
                            <strong>{t('mapping:fields.responsible')}</strong> {area.user} | <strong>{t('mapping:fields.criticity')}</strong> {area.criticity}
                          </Typography>
                          <Typography variant="caption" display="block">
                            <strong>{t('mapping:fields.processes')}</strong> {area.processes?.length || 0} |{' '}
                            <strong>{t('mapping:fields.systems')}</strong> {area.systems?.length || 0} |{' '}
                            <strong>{t('mapping:fields.assets')}</strong> {area.assets?.length || 0}
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography color="text.secondary">{t('mapping:empty.areas')}</Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Processes */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 'bold', color: '#e91e63' }}>
                    {t('mapping:sections.processes', { count: processesCount })}
                  </Typography>
                  <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
                    {companyProcesses?.length > 0 ? (
                      companyProcesses.map((process) => (
                        <Box key={process._id} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, wordBreak: 'break-word' }}>
                            {process.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, wordBreak: 'break-word' }}>
                            {process.description?.length > 100 ? `${process.description.substring(0, 100)}...` : process.description}
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ wordBreak: 'break-word' }}>
                            <strong>{t('mapping:fields.responsible')}</strong> {process.user} | <strong>{t('mapping:fields.criticity')}</strong> {process.criticity}
                          </Typography>
                          <Typography variant="caption" display="block">
                            <strong>{t('mapping:fields.dpia')}</strong> {process.dpia ? t('common:boolean.yes') : t('common:boolean.no')} |{' '}
                            <strong>{t('mapping:fields.lia')}</strong> {process.lia ? t('common:boolean.yes') : t('common:boolean.no')} |{' '}
                            <strong>{t('mapping:fields.data')}</strong> {process.dataMapping?.length || 0}
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography color="text.secondary">{t('mapping:empty.processes')}</Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Systems */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 'bold', color: '#00bcd4' }}>
                    {t('mapping:sections.systems', { count: systemsCount })}
                  </Typography>
                  <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
                    {companySystems?.length > 0 ? (
                      companySystems.map((system) => (
                        <Box key={system._id} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, wordBreak: 'break-word' }}>
                            {system.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, wordBreak: 'break-word' }}>
                            {system.description?.length > 100 ? `${system.description.substring(0, 100)}...` : system.description}
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ wordBreak: 'break-word' }}>
                            <strong>{t('mapping:fields.responsible')}</strong> {system.user} | <strong>{t('mapping:fields.criticity')}</strong> {system.criticity}
                          </Typography>
                          <Typography variant="caption" display="block">
                            <strong>{t('mapping:fields.saas')}</strong> {system.saas ? t('common:boolean.yes') : t('common:boolean.no')} |{' '}
                            <strong>{t('mapping:fields.developedInHouse')}</strong> {system.developedInHouse ? t('common:boolean.yes') : t('common:boolean.no')}
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography color="text.secondary">{t('mapping:empty.systems')}</Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Assets */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 'bold', color: '#4caf50' }}>
                    {t('mapping:sections.assets', { count: assetsCount })}
                  </Typography>
                  <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
                    {companyAssets?.length > 0 ? (
                      companyAssets.map((asset) => (
                        <Box key={asset._id} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, wordBreak: 'break-word' }}>
                            {asset.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, wordBreak: 'break-word' }}>
                            {asset.description?.length > 100 ? `${asset.description.substring(0, 100)}...` : asset.description}
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ wordBreak: 'break-word' }}>
                            <strong>{t('mapping:fields.responsible')}</strong> {asset.user} | <strong>{t('mapping:fields.criticity')}</strong> {asset.criticity}
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ wordBreak: 'break-word' }}>
                            <strong>{t('mapping:fields.type')}</strong> {asset.type} |{' '}
                            <strong>{t('mapping:fields.pii')}</strong> {asset.pii ? t('common:boolean.yes') : t('common:boolean.no')} |{' '}
                            <strong>{t('mapping:fields.location')}</strong> {asset.location}
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography color="text.secondary">{t('mapping:empty.assets')}</Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Main>
    </>
  );
};
